/*
 * Glimpse coverflow carousel.
 *
 * One active card sits centred at full size; neighbours step outwards, shrink,
 * blur and gain a white veil. Cards further out than the visible range are
 * parked just off the visible edge at opacity 0, which is what keeps the
 * wrap-around from dragging a card across the stage.
 *
 * Scoped per section instance (keyed on section.id) and re-initialised on
 * shopify:section:load so the Theme Editor can add, remove and reorder blocks.
 */
(function () {
  'use strict';

  /* ---- config knobs (schema settings override these via data attributes) ---- */
  var DEFAULTS = {
    VISIBLE: 2, // cards either side of centre
    // Figma "Frame 782" (node 7709:79355) measures 411x600 for the centre card,
    // 343x500 one step out and 274x400 two steps out:
    //   343/411 = 0.8345, 274/411 = 0.6667  ->  1/6 per step
    SCALE_STEP: 1 / 6,
    GUTTER_FACTOR: 0.057, // 23.5 / 411: constant gap between adjacent card edges
    DELAY: 3800, // autoplay interval, ms
    DURATION: 850, // transition duration, ms
    EASING: 'cubic-bezier(0.65, 0, 0.35, 1)',
    REDUCED_DURATION: 160, // prefers-reduced-motion
    BLUR: 1.4, // px, non-active cards
    VEIL_BASE: 0.4,
    VEIL_STEP: 0.18,
    VEIL_MAX: 0.6,
    DRAG_THRESHOLD: 45, // px of pointer travel before it counts as a swipe
    CARD_MIN: 180,
    CARD_MAX: 411, // design: centre card is 411px wide
    CARD_RATIO: 0.285, // 411 / 1442 (card width / design frame width)
    CARD_ASPECT: 600 / 411, // design: 411x600 -> 1.4599
    MIN_SCALE: 0.1
  };

  var controllers = new Map();

  function clamp(value, lo, hi) {
    return Math.min(Math.max(value, lo), hi);
  }

  function attrNumber(el, name, fallback) {
    var raw = parseFloat(el.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  function create(root) {
    var stage = root.querySelector('[data-glimpse-stage]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-glimpse-card]'));
    if (!stage || cards.length === 0) return null;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-glimpse-dot]'));
    var prevButton = root.querySelector('[data-glimpse-prev]');
    var nextButton = root.querySelector('[data-glimpse-next]');

    var cfg = {
      VISIBLE: attrNumber(root, 'data-glimpse-visible', DEFAULTS.VISIBLE),
      SCALE_STEP: attrNumber(root, 'data-glimpse-scale-step', DEFAULTS.SCALE_STEP * 100) / 100,
      GUTTER_FACTOR: attrNumber(root, 'data-glimpse-gap', DEFAULTS.GUTTER_FACTOR * 100) / 100,
      DELAY: attrNumber(root, 'data-glimpse-delay', DEFAULTS.DELAY)
    };

    var N = cards.length;

    /*
     * At least one card has to stay parked off-stage or the wrap is visible, so
     * never show more neighbours than the block count can buffer:
     *   6+ blocks -> 2 each side (5 on stage), 4-5 -> 1 each side, <=3 -> centre only.
     */
    var visible = clamp(Math.floor((N - 2) / 2), 0, cfg.VISIBLE);

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var listeners = new AbortController();
    var signal = listeners.signal;

    var current = 0;
    var cardW = 0;
    var cardH = 0;
    var gutter = 0;
    var offsets = []; // distance from centre for step 0..visible+1
    var timer = null;
    var hovered = false;
    var inView = true;
    var resizeObserver = null;
    var viewObserver = null;

    function transitionDuration() {
      return reducedMotion.matches ? DEFAULTS.REDUCED_DURATION : DEFAULTS.DURATION;
    }

    function measure() {
      var width = root.clientWidth || stage.clientWidth || 0;
      cardW = clamp(width * DEFAULTS.CARD_RATIO, DEFAULTS.CARD_MIN, DEFAULTS.CARD_MAX);
      cardH = cardW * DEFAULTS.CARD_ASPECT;
      gutter = cardW * cfg.GUTTER_FACTOR;

      /*
       * Cards are packed edge to edge with a constant gutter rather than on a
       * fixed stride, because each step is narrower than the last. Distance
       * between the centres of step k-1 and step k is
       *   halfWidth(k-1) + gutter + halfWidth(k)
       * which with scale = 1 - k*SCALE_STEP reduces to
       *   cardW/2 * (2 - (2k-1)*SCALE_STEP) + gutter
       * That reproduces the design's 400.5px and 733px centre offsets.
       */
      offsets = [0];
      for (var k = 1; k <= visible + 1; k++) {
        offsets[k] = offsets[k - 1] + (cardW / 2) * (2 - (2 * k - 1) * cfg.SCALE_STEP) + gutter;
      }

      root.style.setProperty('--glimpse-card-w', cardW + 'px');
      root.style.setProperty('--glimpse-card-h', cardH + 'px');
      root.style.setProperty('--glimpse-duration', transitionDuration() + 'ms');
      root.style.setProperty('--glimpse-easing', DEFAULTS.EASING);
      stage.style.height = cardH + 'px';
    }

    function render() {
      cards.forEach(function (card, i) {
        // shortest signed distance from this card to the centre
        var o = ((i - current) % N + N) % N;
        if (o > N / 2) o -= N;

        var dist = Math.abs(o);
        var onStage = dist <= visible;
        var steps = onStage ? dist : visible + 1;
        var x = Math.sign(o) * offsets[steps];
        var scale = Math.max(DEFAULTS.MIN_SCALE, 1 - steps * cfg.SCALE_STEP);

        card.style.transform = 'translate(-50%, -50%) translateX(' + x + 'px) scale(' + scale + ')';
        card.style.opacity = onStage ? '1' : '0';
        card.style.zIndex = onStage ? String(100 - dist) : '0';
        card.style.setProperty('--glimpse-blur', (o === 0 ? 0 : DEFAULTS.BLUR) + 'px');

        var veil = card.querySelector('[data-glimpse-veil]');
        if (veil) {
          var opacity = o === 0
            ? 0
            : Math.min(DEFAULTS.VEIL_MAX, DEFAULTS.VEIL_BASE + (dist - 1) * DEFAULTS.VEIL_STEP);
          veil.style.opacity = String(onStage ? opacity : DEFAULTS.VEIL_MAX);
        }

        card.classList.toggle('is-active', o === 0);
        // parked cards are out of reach for pointers and assistive tech
        card.setAttribute('aria-hidden', onStage ? 'false' : 'true');
        card.style.pointerEvents = onStage ? '' : 'none';
      });

      dots.forEach(function (dot, i) {
        var isActive = i === current;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      syncVideos();
    }

    function syncVideos() {
      cards.forEach(function (card, i) {
        var video = card.querySelector('video');
        if (!video) return;
        if (i === current && inView) {
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    }

    function goTo(index, fromInteraction) {
      current = ((index % N) + N) % N;
      render();
      if (fromInteraction) restartAutoplay();
    }

    function step(direction, fromInteraction) {
      goTo(current + direction, fromInteraction);
    }

    /* ---- autoplay ---- */
    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (N < 2 || hovered || !inView || cfg.DELAY <= 0) return;
      timer = window.setInterval(function () {
        step(1, false);
      }, cfg.DELAY);
    }

    function restartAutoplay() {
      startAutoplay();
    }

    /* ---- wiring ---- */
    if (prevButton) {
      prevButton.addEventListener('click', function () { step(-1, true); }, { signal: signal });
    }
    if (nextButton) {
      nextButton.addEventListener('click', function () { step(1, true); }, { signal: signal });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i, true); }, { signal: signal });
    });

    // a side card centres itself; the centred card follows its link in a new tab
    cards.forEach(function (card, i) {
      card.addEventListener('click', function (event) {
        if (i !== current) {
          event.preventDefault();
          goTo(i, true);
          return;
        }
        // a real anchor inside the card (the overlay arrow) handles itself
        if (event.target.closest && event.target.closest('a')) return;
        var href = card.getAttribute('data-glimpse-link');
        if (!href) return;
        window.open(href, '_blank', 'noopener');
      }, { signal: signal });
    });

    root.addEventListener('mouseenter', function () {
      hovered = true;
      stopAutoplay();
    }, { signal: signal });

    root.addEventListener('mouseleave', function () {
      hovered = false;
      startAutoplay();
    }, { signal: signal });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1, true);
      }
    }, { signal: signal });

    /* ---- pointer drag / swipe ---- */
    var dragId = null;
    var dragStartX = 0;
    var dragHandled = false;

    stage.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragId = event.pointerId;
      dragStartX = event.clientX;
      dragHandled = false;
      stopAutoplay();
    }, { signal: signal });

    stage.addEventListener('pointermove', function (event) {
      if (dragId !== event.pointerId || dragHandled) return;
      var dx = event.clientX - dragStartX;
      if (Math.abs(dx) < DEFAULTS.DRAG_THRESHOLD) return;
      dragHandled = true;
      step(dx < 0 ? 1 : -1, true);
    }, { signal: signal });

    function endDrag(event) {
      if (dragId !== event.pointerId) return;
      dragId = null;
      if (!dragHandled) startAutoplay();
    }

    stage.addEventListener('pointerup', endDrag, { signal: signal });
    stage.addEventListener('pointercancel', endDrag, { signal: signal });

    /* ---- responsive + visibility ---- */
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(function () {
        measure();
        render();
      });
      resizeObserver.observe(root);
    } else {
      window.addEventListener('resize', function () {
        measure();
        render();
      }, { signal: signal });
    }

    if (typeof IntersectionObserver === 'function') {
      viewObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          if (inView) {
            startAutoplay();
          } else {
            stopAutoplay();
          }
          syncVideos();
        });
      }, { threshold: 0.25 });
      viewObserver.observe(root);
    }

    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', measure, { signal: signal });
    }

    // first paint must not animate: lay the cards out with transitions off,
    // then re-enable them once the browser has committed that frame
    root.classList.add('glimpse-coverflow--no-anim');
    root.classList.add('glimpse-coverflow--ready');
    measure();
    render();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.remove('glimpse-coverflow--no-anim');
      });
    });
    startAutoplay();

    return {
      destroy: function () {
        stopAutoplay();
        listeners.abort();
        if (resizeObserver) resizeObserver.disconnect();
        if (viewObserver) viewObserver.disconnect();
        root.classList.remove('glimpse-coverflow--ready');
      }
    };
  }

  function key(root) {
    return root.getAttribute('data-glimpse-section-id') || root.id;
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-glimpse-coverflow]');
    Array.prototype.forEach.call(roots, function (root) {
      var id = key(root);
      var existing = controllers.get(id);
      if (existing) existing.destroy(); // Theme Editor re-render: rebind to fresh DOM
      var controller = create(root);
      if (controller) controllers.set(id, controller);
    });
  }

  function destroyIn(scope) {
    var roots = (scope || document).querySelectorAll('[data-glimpse-coverflow]');
    Array.prototype.forEach.call(roots, function (root) {
      var id = key(root);
      var controller = controllers.get(id);
      if (controller) {
        controller.destroy();
        controllers.delete(id);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyIn(event.target);
  });
})();
